import { supabase } from "../../supabaseClient";
import { getOnboardingCompletionState } from "../onboarding/profileGate";

const PORTAL_INTENT_STORAGE_KEY =
  "gwinPreferredPortalIntent";

const PORTAL_INTENT_MAX_AGE_MS =
  10 * 60 * 1000;

const VALID_PORTALS = [
  "learner",
  "partner",
  "admin",
];

const DEFAULT_LEARNER_ACCESS = {
  account_type: "learner",
  home_path: null,
  access_status: "active",
  is_admin: false,
  partner_member_id: null,
  partner_id: null,
  partner_name: null,
  partner_role: null,
  partner_member_status: null,
  partner_organisation_status: null,
};

const DEFAULT_DUAL_PORTAL_TEST_ACCESS = {
  is_dual_portal_tester: false,
  can_access_learner: false,
  can_access_partner: false,
  tester_partner_member_id: null,
  tester_partner_id: null,
  tester_partner_role: null,
};

function normalisePortal(value) {
  const portal = String(value || "")
    .trim()
    .toLowerCase();

  return VALID_PORTALS.includes(portal)
    ? portal
    : "";
}

export function savePreferredPortalIntent(
  portal
) {
  const cleanPortal =
    normalisePortal(portal);

  if (
    !cleanPortal ||
    typeof window === "undefined"
  ) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      PORTAL_INTENT_STORAGE_KEY,
      JSON.stringify({
        portal: cleanPortal,
        createdAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn(
      "Could not save the preferred portal:",
      error
    );
  }
}

export function clearPreferredPortalIntent() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(
      PORTAL_INTENT_STORAGE_KEY
    );
  } catch (error) {
    console.warn(
      "Could not clear the preferred portal:",
      error
    );
  }
}

function consumePreferredPortalIntent() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const storedValue =
      window.sessionStorage.getItem(
        PORTAL_INTENT_STORAGE_KEY
      );

    if (!storedValue) {
      return "";
    }

    window.sessionStorage.removeItem(
      PORTAL_INTENT_STORAGE_KEY
    );

    const parsed =
      JSON.parse(storedValue);

    const portal =
      normalisePortal(parsed?.portal);

    const createdAt =
      Number(parsed?.createdAt || 0);

    const hasExpired =
      !createdAt ||
      Date.now() - createdAt >
        PORTAL_INTENT_MAX_AGE_MS;

    if (
      !portal ||
      hasExpired
    ) {
      return "";
    }

    return portal;
  } catch (error) {
    console.warn(
      "Could not read the preferred portal:",
      error
    );

    clearPreferredPortalIntent();

    return "";
  }
}

export async function getMyPortalAccess() {
  const { data, error } =
    await supabase.rpc(
      "get_my_portal_access"
    );

  if (error) {
    throw error;
  }

  const access =
    Array.isArray(data)
      ? data[0]
      : data;

  return (
    access ||
    DEFAULT_LEARNER_ACCESS
  );
}

export async function getMyDualPortalTestAccess() {
  const { data, error } =
    await supabase.rpc(
      "get_my_dual_portal_test_access"
    );

  if (error) {
    throw error;
  }

  const access =
    Array.isArray(data)
      ? data[0]
      : data;

  return (
    access ||
    DEFAULT_DUAL_PORTAL_TEST_ACCESS
  );
}

export function isAdminAccess(access) {
  return (
    access?.account_type ===
      "admin" ||
    access?.is_admin === true
  );
}

export function isActivePartnerAccess(
  access
) {
  return (
    access?.account_type ===
      "partner" &&
    access?.access_status ===
      "active" &&
    access?.partner_member_status ===
      "active" &&
    access
      ?.partner_organisation_status ===
      "active"
  );
}

export function isActiveDualPortalTester(
  dualPortalAccess
) {
  return (
    dualPortalAccess
      ?.is_dual_portal_tester ===
    true
  );
}

export function canUseLearnerPortal({
  access,
  dualPortalAccess,
}) {
  if (
    access?.account_type ===
    "learner"
  ) {
    return true;
  }

  return (
    isActiveDualPortalTester(
      dualPortalAccess
    ) &&
    dualPortalAccess
      ?.can_access_learner === true
  );
}

export function canUsePartnerPortal({
  access,
  dualPortalAccess,
}) {
  if (
    isActivePartnerAccess(access)
  ) {
    return true;
  }

  return (
    isActiveDualPortalTester(
      dualPortalAccess
    ) &&
    dualPortalAccess
      ?.can_access_partner === true
  );
}

export function getPartnerAccessMessage(
  status
) {
  switch (
    String(status || "")
      .toLowerCase()
  ) {
    case "invited":
      return "Your partner invitation has not been completed yet.";

    case "suspended":
      return "Your partner account has been suspended. Please contact Gwin support.";

    case "organisation_suspended":
      return "This partner organisation has been suspended. Please contact Gwin support.";

    case "organisation_archived":
      return "This partner organisation is no longer active.";

    case "inactive":
      return "Your partner access is currently inactive.";

    case "error":
      return "We could not verify your portal access. Please try again.";

    default:
      return "";
  }
}

function partnerLoginPath(status) {
  const value =
    String(status || "inactive");

  return (
    `/partner/login?access=` +
    encodeURIComponent(value)
  );
}

async function resolveLearnerDestination({
  access,
  userId,
  beforeLearnerResolve,
}) {
  if (
    typeof beforeLearnerResolve === "function"
  ) {
    await beforeLearnerResolve();
  }

  const onboardingState =
    await getOnboardingCompletionState();

  const onboardingCompleted =
    onboardingState?.onboardingCompleted === true;

  return {
    destination: onboardingCompleted
      ? "/dashboard"
      : "/profile-gate",

    access:
      access ||
      DEFAULT_LEARNER_ACCESS,
  };
}

export async function resolveDestinationFromAccess({
  access,
  userId,
  beforeLearnerResolve,
  preferredPortal = "",
  dualPortalAccess =
    DEFAULT_DUAL_PORTAL_TEST_ACCESS,
}) {
  if (!userId) {
    return {
      destination: "/login",
      access: access || null,
    };
  }

  const cleanPreferredPortal =
    normalisePortal(
      preferredPortal
    );

  /*
   * Tester-only learner preference.
   *
   * A partner or admin account only reaches
   * the learner dashboard here when it is on
   * the explicit dual portal tester allowlist.
   */
  if (
    cleanPreferredPortal ===
      "learner" &&
    canUseLearnerPortal({
      access,
      dualPortalAccess,
    })
  ) {
    return resolveLearnerDestination({
      access,
      userId,
      beforeLearnerResolve,
    });
  }

  /*
   * Partner preference.
   *
   * Normal active partners already pass this
   * check. It also supports allowlisted test
   * users that have valid partner access.
   */
  if (
    cleanPreferredPortal ===
      "partner" &&
    canUsePartnerPortal({
      access,
      dualPortalAccess,
    })
  ) {
    return {
      destination: "/partner",
      access,
    };
  }

  /*
   * Admin preference remains restricted to
   * genuine Gwin administrator accounts.
   */
  if (
    cleanPreferredPortal ===
      "admin" &&
    isAdminAccess(access)
  ) {
    return {
      destination: "/admin",
      access,
    };
  }

  /*
   * Existing default routing remains unchanged
   * for all normal accounts.
   */
  if (isAdminAccess(access)) {
    return {
      destination: "/admin",
      access,
    };
  }

  if (
    access?.account_type ===
    "partner"
  ) {
    if (
      isActivePartnerAccess(access)
    ) {
      return {
        destination: "/partner",
        access,
      };
    }

    return {
      destination:
        partnerLoginPath(
          access?.access_status
        ),

      access,
    };
  }

  return resolveLearnerDestination({
    access,
    userId,
    beforeLearnerResolve,
  });
}

export async function resolvePostLoginDestination({
  userId,
  beforeLearnerResolve,
  preferredPortal = "",
}) {
  const storedPortal =
    preferredPortal
      ? ""
      : consumePreferredPortalIntent();

  const cleanPreferredPortal =
    normalisePortal(
      preferredPortal ||
        storedPortal
    );

  const access =
    await getMyPortalAccess();

  let dualPortalAccess =
    DEFAULT_DUAL_PORTAL_TEST_ACCESS;

  /*
   * Only make the additional tester access
   * request when a portal preference matters.
   */
  if (cleanPreferredPortal) {
    dualPortalAccess =
      await getMyDualPortalTestAccess();
  }

  return resolveDestinationFromAccess({
    access,
    userId,
    beforeLearnerResolve,
    preferredPortal:
      cleanPreferredPortal,
    dualPortalAccess,
  });
}